import classNames from "classnames";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { TooltipComponent } from "@fidget/common/components/tooltipComponent";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { logger } from "@fidget/common/utils";
import { caseInsensitiveIncludes } from "@fidget/common/utils/searchMatching";
import { useStore } from "../../models";
import { IIntentSuggestion, IntentResponseContext } from "../../models/intent/IntentStore";
import { IIntent } from "../../models/room/chat/ChatIntent";
import { InputType } from "../../models/room/ChatInput";
import { AutoCompleteSearchingInput } from "../dashboard/searchingInput/autoCompleteSearchingInput";
import styles from "./ChatIntent.css";
import { ControllerStateAndHelpers } from "downshift";
import { AppPermission } from "@closerplatform/spinner-openapi";
import { useAgentServices } from "../../models/agentServices";
import { ITagsChangeChatItem } from "../../models/room/chat/items";
import { ChatItemAssertion } from "../../models/room/chat/ChatItemAssertion";

interface Props {
  intent?: IIntent;
  messageId: string;
  parentRef?: HTMLDivElement;
}

enum IntentState {
  ACTIVE, // all operations (appendText/send) are available.
  TEXT_APPENDED, // the text has been appended to the inputChat. This state blocks all operations.
  SENT // the text has been sent. This state blocks all operations.
}

export const ChatIntent = observer(({ intent, messageId, parentRef }: Props) => {
  const { spinnerService } = useAgentServices();
  const { roomStore, intentStore, uiStore, currentUserStore, tagGroupsStore, tagsStore } =
    useStore();
  const [intentState, setIntentState] = useState<IntentState>(IntentState.ACTIVE);
  const [classifyMessageClicked, setClassifyMessageClicked] = useState<boolean>(false);
  const [intentClassified, setIntentClassified] = useState<boolean>(false);
  const [classifyButtonRef, setClassifyButtonRef] = useState<HTMLDivElement>();

  const suggestions = intentStore.getIntentSuggestions(
    roomStore.currentRoom?.languageOption || currentUserStore.currentLanguage
  );

  const context: IntentResponseContext = {
    agent: currentUserStore.profile,
    client: roomStore.currentRoom?.owner
  };

  const intentResponse =
    intent?.intentSuggestion?.active && intent?.intentSuggestion?.getCompiledResponse(context);

  const inputWidth = 244;

  const isDisabled = () => {
    switch (intentState) {
      case IntentState.SENT:
      case IntentState.TEXT_APPENDED:
        return true;
      default:
        return false;
    }
  };

  const sendIntentText = () => {
    const { currentRoom } = roomStore;

    if (!currentRoom) {
      return;
    }

    if (intentResponse) {
      setIntentState(IntentState.SENT);
      currentRoom.sendMessage(intentResponse);
      setTimeout(() => setIntentState(IntentState.ACTIVE), 2000);
    }
  };

  const insertIntentText = () => {
    const { currentRoom } = roomStore;

    if (!currentRoom) {
      return;
    }

    const { chatInput } = currentRoom;
    if (intentResponse) {
      chatInput.setInputType(InputType.Message);
      setIntentState(IntentState.TEXT_APPENDED);
      chatInput.appendText(intentResponse);
      setTimeout(() => setIntentState(IntentState.ACTIVE), 2000);
    }
  };

  const onIntentClassified = (pickedIntent: IIntentSuggestion) => {
    setClassifyMessageClicked(false);

    const currentRoom = roomStore.currentRoom;
    if (currentRoom) {
      const chatItems = currentRoom.chat.chatItems;
      const message = chatItems.find((item) => item.id === messageId);
      const tagsChangeItems: Array<ITagsChangeChatItem> = chatItems.filter((item) =>
        ChatItemAssertion.isTagsChange(item)
      );
      if (ChatItemAssertion.isUserText(message)) {
        const messageText = message.text;
        const messageTagGroupId =
          tagsChangeItems
            .filter((item) => item.timestamp > message.timestamp)
            .map((item) => item.eventData.oldTagGroupId)[0] || currentRoom.owner.tagGroupId;
        const messageTagGroup = messageTagGroupId
          ? tagGroupsStore.getById(messageTagGroupId)
          : undefined;
        const messageTags = messageTagGroup ? tagsStore.getFromIds(messageTagGroup.tagIds) : [];
        const messageContext: Array<string> = messageTags.map((tag) => tag.name);

        spinnerService
          .createDatasetEntry(pickedIntent.id, { text: messageText, context: messageContext })
          .catch((e) => logger.error({ message: "Couldn't classify chat message", error: e }))
          .finally(() => setIntentClassified(true));
      }
    }
  };

  const classifyButtonDistanceFromLeftChatEdge = () => {
    if (!parentRef || !classifyButtonRef) {
      return undefined;
    }

    const leftChatEdge = parentRef.getBoundingClientRect().left - 50;
    const rightButtonEdge = classifyButtonRef.getBoundingClientRect().right;

    return rightButtonEdge - leftChatEdge;
  };

  const renderIntentClassifySearch = () => {
    const itemProvider = ({ inputValue }: ControllerStateAndHelpers<IIntentSuggestion>) => {
      return inputValue
        ? suggestions.filter((item) => caseInsensitiveIncludes(item.intent, inputValue))
        : suggestions;
    };

    const distance = classifyButtonDistanceFromLeftChatEdge();
    const shouldBeDisplayedOnLeft = !!distance && distance > inputWidth;

    return (
      <div
        className={classNames(styles["classify-input"], {
          [styles["classify-input-left"]]: shouldBeDisplayedOnLeft
        })}
      >
        <AutoCompleteSearchingInput
          itemProvider={itemProvider}
          onChange={onIntentClassified}
          mapItemToString={(item) => item?.intent || ""}
          placeholder={intl.formatMessage("ai.modelConfiguration.intent.form.name.placeholder")}
          notFoundText={intl.formatMessage("ai.modelConfiguration.intents.searchNotFound")}
          itemClassName={styles["classify-item"]}
          isOpen
        />
      </div>
    );
  };

  const renderIntent = () => {
    return (
      <TooltipWrapper
        message={`${intl
          .formatMessage("conversation.event.intent.edit")
          .toUpperCase()} ${intentResponse}`}
      >
        <button
          type="button"
          className={classNames(styles.edit)}
          onClick={insertIntentText}
          disabled={isDisabled()}
        >
          <span className={styles["intent-name"]}>/{intent?.displayName}</span>
          {intentState === IntentState.TEXT_APPENDED ? (
            <span className={classNames(styles.icon, styles["in-use"])}>↴</span>
          ) : (
            <i className={classNames("icon-pencil", styles.icon)} />
          )}
        </button>
      </TooltipWrapper>
    );
  };

  const renderSendButton = () => (
    <TooltipWrapper
      message={`${intl
        .formatMessage("conversation.event.intent.send")
        .toUpperCase()} ${intentResponse}`}
    >
      <button onClick={sendIntentText} className={styles.send} disabled={isDisabled()}>
        <i className={classNames("icon-paper-plane-empty", styles["send-icon"])} />
      </button>
    </TooltipWrapper>
  );

  const renderClassifyButton = () => (
    <div ref={(el) => setClassifyButtonRef(el || undefined)}>
      <TooltipComponent
        text={intl.formatMessage("conversation.event.intent.classify")}
        positionPreset="top"
        minWidth="110px"
      >
        <button
          onClick={() => setClassifyMessageClicked(!classifyMessageClicked)}
          className={styles.send}
          disabled={isDisabled()}
        >
          <i className={classNames("icon-swap", styles["send-icon"])} />
        </button>
      </TooltipComponent>
    </div>
  );

  const render = () => {
    const shouldRenderClassifyButton =
      !intentClassified &&
      suggestions &&
      suggestions.length > 0 &&
      uiStore.canDisplayWithPermission(AppPermission.AiIntentsClassify);

    return (
      <div className={classNames(styles.main)}>
        {intentResponse && renderIntent()}
        {intentResponse && renderSendButton()}
        {shouldRenderClassifyButton && renderClassifyButton()}
        {classifyMessageClicked && renderIntentClassifySearch()}
      </div>
    );
  };

  return render();
});

interface TooltipWrapperProps {
  children: JSX.Element[] | JSX.Element;
  message: string;
}

const TooltipWrapper = (props: TooltipWrapperProps) => {
  return (
    <TooltipComponent
      style="dashboard"
      positionPreset={"top"}
      text={props.message}
      maxWidth={"200px"}
      width={"200px"}
      changeTransformOutset={"translate(20px, -1px)"}
    >
      {props.children}
    </TooltipComponent>
  );
};
